interface Info {
  label: string;
  value: string | null;
}

interface CreditSum {
  label: string;
  value: string;
  sum: boolean;
}

interface ShopInfoSectionProps {
  customerInfo: Info[];
}

interface CreditDetailsSectionProps {
  detailsCredit: Info[];
  creditDetails: Info[];
  creditSum: CreditSum[];
  cusComId?: any;
}

interface CreditRequestLayoutProps {
  creditTabs: string | null;
  creditRequest: CreditLimitEntity | orderOverCreditsEntities | null;
  creditImageFiles?: string[];
  data: {
    customerCompanyId?: string;
    orderNo?: string;
    customerNo?: string;
    customerName?: string;
    taxNo?: string;
    ownerFirstname?: string;
    ownerLastname?: string;
    telephone?: string;
    zone?: string;
    primaryCreditLimitUsed?: string;
    termPaymentType?: string;
    province?: string;
  };
}
import { Col, Row } from "antd";
import Text from "../../../components/Text/Text";
import Descriptions from "../../../components/Description/Descriptions";
import DescSumOrder from "../../../components/Description/DescSumOrder";
import { color } from "../../../resource";
import { useState } from "react";
import { createCreditRequestData } from "../../../definitions/creditRequestData";
import ModalInfoCredit from "../../../components/Modal/ModalInfoCredit";
import { numberFormatter } from "../../../utility/Formatter";
import { shopDetailFields } from "../../../definitions/creditLimit";
import type { CreditLimitEntity } from "../../../entities/CreditLimitEntity";
import type { orderOverCreditsEntities } from "../../../entities/OverCreditApproveEntity";
import { ImagePreviewModal } from "../../../components/Modal/ImagePreviewModal";
import NewModalInfoCredit from "../../../components/Modal/NewModalInfoCredit";

const styles = {
  sectionTitle: {
    margin: "18px 0px",
  },
  headerContainer: {
    margin: "18px 0px",
    display: "flex",
    justifyContent: "space-between",
    width: "100%",
  },
  descriptionStyle: {
    padding: "6px 0px",
  },
};

const ShopInfoSection = ({
  customerInfo,
  province,
}: ShopInfoSectionProps & { province: string }) => (
  <Col span={12}>
    <div style={styles.sectionTitle}>
      <Text fontWeight={700}>ข้อมูลร้านค้า</Text>
    </div>
    {customerInfo.map(({ label, value }, index) => (
      <Descriptions
        key={`shop-${index}`}
        label={label}
        value={value || "-"}
        province={province}
        leftSpan={5}
        style={styles.descriptionStyle}
        noColon
      />
    ))}
  </Col>
);

const CreditDetailsSection = ({
  detailsCredit,
  creditDetails,
  creditSum,
  cusComId,
}: CreditDetailsSectionProps) => {
  const [infoCreditModal, setInfoCreditModal] = useState<{
    open: boolean;
    cusComId: any;
  }>({ open: false, cusComId: null });

  return (
    <Col span={12}>
      <Row justify='space-between'>
        <div style={styles.headerContainer}>
          <Text fontWeight={700}>รายละเอียดคำขอวงเงิน</Text>
          <u
            style={{ cursor: "pointer", color: color.primary }}
            onClick={() => setInfoCreditModal({ open: true, cusComId: cusComId })}
          >
            <Text color='primary'>เช็ควงเงินเครดิตร้าน</Text>
          </u>
        </div>
      </Row>

      {detailsCredit.map(({ label, value }, index) => (
        <Descriptions
          key={`credit-${index}`}
          label={label}
          value={value || "-"}
          leftSpan={6}
          style={styles.descriptionStyle}
          noColon
        />
      ))}

      {creditDetails.map(({ label, value }, index) => (
        <div key={`limit-${index}`} style={{ marginTop: index === 0 ? "18px" : "0px" }}>
          <DescSumOrder label={label} value={value || "-"} leftSpan={6} noColon sum={false} />
        </div>
      ))}

      {creditSum.map(({ label, value, sum }, index) => (
        <div key={`sum-${index}`} style={{ marginTop: "0px" }}>
          {sum && <div />}
          <DescSumOrder label={label} value={value || "-"} leftSpan={6} noColon sum={sum} />
        </div>
      ))}
      {infoCreditModal.open && (
        <NewModalInfoCredit
          open={infoCreditModal.open}
          onClose={() => setInfoCreditModal({ open: false, cusComId: null })}
          cusComId={infoCreditModal.cusComId}
        />
      )}
    </Col>
  );
};

const CreditRequestLayout = ({
  data,
  creditRequest,
  creditTabs,
  creditImageFiles,
}: CreditRequestLayoutProps) => {
  const [openInfoCredit, setOpenInfoCredit] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewIndex, setPreviewIndex] = useState(0);
  const handlePreview = (index: number) => {
    setPreviewIndex(index);
    setPreviewOpen(true);
  };

  const {
    customerDetails: {
      customerInfo,
      creditRequestInfo: detailsCredit,
      creditLimitInfo: { details: creditDetails, summary: creditSum },
    },
  } = createCreditRequestData(
    data,
    creditRequest,
    (creditTabs ?? "ORDER") as "ORDER" | "CREDIT",
    creditImageFiles,
    handlePreview,
  );
  console.log("data", data);

  return (
    <Row gutter={[20, 20]}>
      <ShopInfoSection customerInfo={customerInfo} province={data?.province || ""} />
      <CreditDetailsSection
        detailsCredit={detailsCredit}
        creditDetails={creditDetails}
        creditSum={creditSum}
        cusComId={data?.customerCompanyId}
      />

      <ModalInfoCredit
        open={openInfoCredit}
        onClose={() => setOpenInfoCredit(false)}
        shopName={data.customerName || ""}
        zone={data.zone || ""}
        termPaymentType={data.termPaymentType || ""}
        province={data?.province || ""}
        data={shopDetailFields.map(({ key, label, labelEn, format, subLabel }) => {
          const value = data[key] || 0;
          const percent = data[`${key}Percent`] || 0;
          const formattedValue = numberFormatter(value, format === "currency" ? 2 : 0);
          const formattedPercent = numberFormatter(percent, format === "currency" ? 2 : 0);
          const isUsed =
            key === "creditLimit" ? data["primaryCreditLimitUsed"] || 0 : data[`${key}Used`] || 0;
          const isBalance =
            key === "creditLimit"
              ? data["primaryCreditLimitBalance"] || 0
              : data[`${key}Balance`] || 0;

          return {
            label,
            labelEn,
            value: formattedValue,
            percent: formattedPercent,
            subLabel,
            used: isUsed,
            remain: isBalance,
          };
        })}
      />
      <ImagePreviewModal
        open={previewOpen}
        images={creditImageFiles || []}
        initialIndex={previewIndex}
        onClose={() => setPreviewOpen(false)}
      />
    </Row>
  );
};

export default CreditRequestLayout;
